import {
  formatEcho,
  formatSig,
  type NumberFormatter,
} from "@/lib/tool-kit/physics-fields/format";
import {
  type PendulumSolution,
  type PendulumTarget,
  seriesFactor,
} from "@/lib/tool-kit/physics-fields/pendulum";

/** Equation, substitution and answer for each unknown, in SI. Symbols are not translated. */
export function pendulumWorking(
  target: PendulumTarget,
  s: PendulumSolution,
  correct: boolean,
  fmt: NumberFormatter,
  sig: number
): string[] {
  const e = (v: number) => formatEcho(fmt, v);
  const r = (v: number, unit: string) => `${formatSig(fmt, v, sig)} ${unit}`;
  const lines: string[] = [];
  const degrees = s.amplitudeDegrees;
  const radians = degrees === null ? 0 : (degrees * Math.PI) / 180;
  if (correct && degrees !== null && target !== "period") {
    lines.push(
      `C = 1 / AGM(1, cos(θ₀/2)) = ${formatSig(fmt, s.factor, 6)}   (θ₀ = ${e(degrees)}°)`,
      `T₀ = T / C = ${e(s.period)} / ${formatSig(fmt, s.factor, 6)} = ${r(s.smallAnglePeriod, "s")}`
    );
  }
  if (target === "period") {
    lines.push(
      "T₀ = 2π √(L / g)",
      `T₀ = 2π × √(${e(s.length)} / ${e(s.gravity)}) = ${r(s.smallAnglePeriod, "s")}`
    );
    if (correct && degrees !== null) {
      lines.push(
        `C = 1 / AGM(1, cos(θ₀/2)) = ${formatSig(fmt, s.factor, 6)}   (θ₀ = ${e(degrees)}°)`,
        `T = T₀ × C = ${e(s.smallAnglePeriod)} × ${formatSig(fmt, s.factor, 6)} = ${r(s.period, "s")}`,
        `1 + θ₀²/16 + 11θ₀⁴/3072 = ${formatSig(fmt, seriesFactor(radians, 3), 6)}`
      );
    }
  } else if (target === "length") {
    lines.push(
      "L = g T₀² / (4π²)",
      `L = ${e(s.gravity)} × (${e(s.smallAnglePeriod)})² / (4π²) = ${r(s.length, "m")}`
    );
  } else {
    lines.push(
      "g = 4π² L / T₀²",
      `g = 4π² × ${e(s.length)} / (${e(s.smallAnglePeriod)})² = ${r(s.gravity, "m/s²")}`
    );
  }
  if (s.maxSpeed !== null) {
    lines.push(`v_max = √(2 g L (1 − cos θ₀)) = ${r(s.maxSpeed, "m/s")}`);
  }
  return lines;
}
